import { useEffect, useRef, useState } from "react";
import { Upload, FileText, Image as ImageIcon, Loader2, X, CheckCircle2, AlertTriangle, Sparkles, UserPlus } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { healthApi } from "@/lib/api";

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}
const stripPrefix = (dataUrl) => (typeof dataUrl === "string" && dataUrl.includes(",") ? dataUrl.split(",")[1] : dataUrl);

const RELATIONS = ["spouse", "father", "mother", "son", "daughter", "brother", "sister", "other"];

export default function UploadRecordDialog({ open, onOpenChange, userId, patientFilters = [], onUploaded, language = "en" }) {
  const pdfRef = useRef(null);
  const imgRef = useRef(null);
  const [step, setStep] = useState("member");
  const [pages, setPages] = useState([]); // {dataUrl, isPdf, name}
  const [progress, setProgress] = useState(0);
  const [message, setMessage] = useState("");
  const [result, setResult] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null); // null => self
  const [addingNew, setAddingNew] = useState(false);
  const [newName, setNewName] = useState("");
  const [newRelation, setNewRelation] = useState("other");

  useEffect(() => {
    if (open) {
      setStep("member");
      setPages([]);
      setProgress(0);
      setMessage("");
      setResult(null);
      setSelectedMember(null);
      setAddingNew(false);
      setNewName("");
      setNewRelation("other");
    }
  }, [open]);

  const members = patientFilters.filter((p) => p.type === "self" || p.type === "dependent");

  const handlePickPdf = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const dataUrl = await fileToDataUrl(file);
    setPages([{ dataUrl, isPdf: true, name: file.name }]);
    setStep("preview");
  };

  const handlePickImages = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (files.length === 0) return;
    const mapped = await Promise.all(files.map(async (f) => ({ dataUrl: await fileToDataUrl(f), isPdf: false, name: f.name })));
    setPages((prev) => [...prev, ...mapped]);
    setStep("preview");
  };

  const removePage = (idx) => setPages((prev) => prev.filter((_, i) => i !== idx));

  const pollStatus = async (recordId, fallback) => {
    for (let i = 0; i < 40; i++) {
      await new Promise((r) => setTimeout(r, 3000));
      try {
        const res = await healthApi.status(recordId, userId);
        const st = res.data?.status;
        if (st === "complete" || st === "failed") return { ...fallback, ...(res.data?.record || {}), status: st };
      } catch (e) {
        /* keep polling */
      }
    }
    return { ...fallback, status: "processing" };
  };

  // Step 1: SAVE the record only (no analysis), then show the Analyze option.
  const saveRecord = async () => {
    if (pages.length === 0) return;
    setStep("analyzing");
    setProgress(30);
    setMessage(pages.length > 1 ? `Saving ${pages.length} pages…` : "Saving document…");
    const hasPdf = pages.some((p) => p.isPdf);
    try {
      const additionalPages = pages.length > 1 && !hasPdf ? pages.slice(1).map((p) => stripPrefix(p.dataUrl)) : undefined;
      const res = await healthApi.uploadBase64({
        userId,
        documentType: "auto",
        documentName: `Health Document - ${new Date().toLocaleDateString()}${pages.length > 1 ? ` (${pages.length} pages)` : ""}`,
        fileType: hasPdf ? "pdf" : "image",
        documentData: pages[0].dataUrl,
        isPdf: hasPdf,
        additionalPages,
        pageCount: pages.length,
        language,
        analyze: false, // SAVE ONLY — analysis is triggered separately
      });
      const rec = res.data?.record || {};

      // Tag to a family member / new dependent if not self
      const chosen = selectedMember;
      if (rec._id && chosen && chosen.type !== "self") {
        try {
          await healthApi.updateRecordPatient(rec._id, userId, {
            patientName: chosen.name,
            patientType: "dependent",
            dependentId: chosen.id && chosen.id !== "new" ? chosen.id : null,
            relation: chosen.relation || null,
          });
        } catch (e) {
          /* non-blocking */
        }
      }

      setProgress(100);
      setResult(rec);
      setStep("saved");
      onUploaded?.(rec);
    } catch (err) {
      setStep("error");
      setMessage(err?.response?.data?.detail || "Failed to save document. Please try again.");
    }
  };

  // Step 2: Trigger AI analysis on the saved record, then poll for the result.
  const startAnalysis = async () => {
    const recordId = result?._id;
    if (!recordId) return;
    setStep("analyzing");
    setProgress(30);
    setMessage("AI is reading & categorizing your document…");
    try {
      await healthApi.analyze(recordId, userId, language);
      setProgress(70);
      setMessage("AI is analyzing your document…");
      const rec = await pollStatus(recordId, result);
      setProgress(100);
      if (rec.status === "failed") {
        setStep("error");
        setMessage("AI could not analyze this document. You can try again.");
        return;
      }
      setResult(rec);
      setStep("success");
      onUploaded?.(rec);
    } catch (err) {
      setStep("error");
      setMessage(err?.response?.data?.detail || "Failed to analyze document. Please try again.");
    }
  };

  const proceedFromMember = () => {
    if (addingNew) {
      if (!newName.trim()) {
        toast.error("Enter the member's name");
        return;
      }
      setSelectedMember({ id: "new", name: newName.trim(), type: "dependent", relation: newRelation });
    }
    setStep("format");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md rounded-3xl" data-testid="upload-record-dialog">
        <DialogHeader>
          <DialogTitle className="text-left">
            {step === "member" && "Who is this report for?"}
            {step === "format" && "Choose file type"}
            {step === "preview" && "Review & save"}
            {step === "analyzing" && "Working…"}
            {step === "saved" && "Record saved"}
            {step === "success" && "Analysis complete"}
            {step === "error" && "Something went wrong"}
          </DialogTitle>
        </DialogHeader>

        {step === "member" && (
          <div className="space-y-2">
            <button
              type="button"
              onClick={() => { setSelectedMember(null); setAddingNew(false); }}
              className={`w-full flex items-center gap-3 rounded-2xl border p-3 text-left ${!addingNew && !selectedMember ? "border-primary bg-primary/5" : "border-border"}`}
              data-testid="upload-member-self"
            >
              <span className="text-xl">👤</span>
              <span className="font-medium flex-1">Myself</span>
            </button>
            {members.filter((m) => m.type === "dependent").map((m) => (
              <button
                key={m.id}
                type="button"
                onClick={() => { setSelectedMember(m); setAddingNew(false); }}
                className={`w-full flex items-center gap-3 rounded-2xl border p-3 text-left ${selectedMember?.id === m.id ? "border-primary bg-primary/5" : "border-border"}`}
                data-testid={`upload-member-${m.id}`}
              >
                <span className="text-xl">👥</span>
                <span className="font-medium flex-1 capitalize">{m.name}{m.relation ? ` · ${m.relation}` : ""}</span>
              </button>
            ))}
            {addingNew ? (
              <div className="rounded-2xl border border-primary bg-primary/5 p-3 space-y-2">
                <Input placeholder="Member name" value={newName} onChange={(e) => setNewName(e.target.value)} data-testid="new-member-name" />
                <div className="flex flex-wrap gap-1.5">
                  {RELATIONS.map((r) => (
                    <button key={r} type="button" onClick={() => setNewRelation(r)} className={`text-xs px-2.5 py-1 rounded-full border capitalize ${newRelation === r ? "border-primary bg-primary text-primary-foreground" : "border-border"}`}>{r}</button>
                  ))}
                </div>
              </div>
            ) : (
              <button type="button" onClick={() => { setAddingNew(true); setSelectedMember(null); }} className="w-full flex items-center gap-3 rounded-2xl border border-dashed border-border p-3 text-left text-muted-foreground" data-testid="upload-add-member">
                <UserPlus className="h-5 w-5" />
                <span className="font-medium flex-1">Add a family member</span>
              </button>
            )}
            <Button className="w-full rounded-2xl mt-2" onClick={proceedFromMember} data-testid="upload-member-continue">Continue</Button>
          </div>
        )}

        {step === "format" && (
          <div className="space-y-3">
            <input ref={pdfRef} type="file" accept="application/pdf" className="hidden" onChange={handlePickPdf} data-testid="pdf-input" />
            <input ref={imgRef} type="file" accept="image/*" multiple className="hidden" onChange={handlePickImages} data-testid="image-input" />
            <button type="button" onClick={() => pdfRef.current?.click()} className="w-full flex items-center gap-3 rounded-2xl border border-border p-4 text-left hover:bg-muted" data-testid="choose-pdf">
              <div className="h-11 w-11 rounded-2xl bg-accent flex items-center justify-center"><FileText className="h-5 w-5 text-primary" /></div>
              <div className="flex-1"><p className="font-medium">Upload a PDF</p><p className="text-xs text-muted-foreground">Lab reports, discharge summaries</p></div>
            </button>
            <button type="button" onClick={() => imgRef.current?.click()} className="w-full flex items-center gap-3 rounded-2xl border border-border p-4 text-left hover:bg-muted" data-testid="choose-images">
              <div className="h-11 w-11 rounded-2xl bg-accent flex items-center justify-center"><ImageIcon className="h-5 w-5 text-primary" /></div>
              <div className="flex-1"><p className="font-medium">Upload images</p><p className="text-xs text-muted-foreground">Photos/scans · multiple pages supported</p></div>
            </button>
            <Button variant="ghost" className="w-full rounded-2xl" onClick={() => setStep("member")}>Back</Button>
          </div>
        )}

        {step === "preview" && (
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-2">
              {pages.map((p, i) => (
                <div key={i} className="relative rounded-xl border border-border overflow-hidden aspect-square bg-muted flex items-center justify-center" data-testid={`preview-page-${i}`}>
                  {p.isPdf ? (
                    <div className="flex flex-col items-center text-muted-foreground"><FileText className="h-7 w-7" /><span className="text-[10px] mt-1 px-1 truncate w-full text-center">{p.name}</span></div>
                  ) : (
                    <img src={p.dataUrl} alt={`page ${i + 1}`} className="h-full w-full object-cover" />
                  )}
                  <button type="button" onClick={() => removePage(i)} className="absolute top-1 right-1 h-6 w-6 rounded-full bg-black/60 text-white flex items-center justify-center" data-testid={`remove-page-${i}`}><X className="h-3.5 w-3.5" /></button>
                </div>
              ))}
              {!pages.some((p) => p.isPdf) && (
                <button type="button" onClick={() => imgRef.current?.click()} className="rounded-xl border border-dashed border-border aspect-square flex items-center justify-center text-muted-foreground"><Upload className="h-5 w-5" /></button>
              )}
            </div>
            <input ref={imgRef} type="file" accept="image/*" multiple className="hidden" onChange={handlePickImages} />
            <Button className="w-full rounded-2xl" onClick={saveRecord} disabled={pages.length === 0} data-testid="save-record-button"><CheckCircle2 className="h-4 w-4" /> Save record</Button>
            <Button variant="ghost" className="w-full rounded-2xl" onClick={() => setStep("format")}>Back</Button>
          </div>
        )}

        {step === "saved" && (
          <div className="space-y-3">
            <div className="flex flex-col items-center py-2">
              <CheckCircle2 className="h-12 w-12 text-emerald-500" />
              <p className="font-semibold mt-2">Record saved securely</p>
              <p className="text-sm text-muted-foreground text-center mt-1">Run AI analysis now to get a simple summary and key findings.</p>
            </div>
            <Button className="w-full rounded-2xl" onClick={startAnalysis} data-testid="analyze-record-button"><Sparkles className="h-4 w-4" /> Analyze with AI</Button>
            <Button variant="ghost" className="w-full rounded-2xl" onClick={() => onOpenChange(false)} data-testid="analyze-later-button">Maybe later</Button>
          </div>
        )}

        {step === "analyzing" && (
          <div className="py-8 flex flex-col items-center gap-4">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <div className="w-full">
              <div className="h-2 rounded-full bg-muted overflow-hidden"><div className="h-full bg-primary transition-all" style={{ width: `${progress}%` }} /></div>
              <p className="text-sm text-muted-foreground text-center mt-3">{message}</p>
            </div>
          </div>
        )}

        {step === "success" && (
          <div className="space-y-3">
            <div className="flex flex-col items-center py-2">
              <CheckCircle2 className="h-12 w-12 text-emerald-500" />
              <p className="font-semibold mt-2">{result?.status === "processing" ? "Uploaded — still analyzing" : "Document analyzed!"}</p>
              {result?.aiAnalysis?.summary && <p className="text-sm text-muted-foreground text-center mt-1 line-clamp-3">{result.aiAnalysis.summary}</p>}
            </div>
            <Button className="w-full rounded-2xl" onClick={() => onOpenChange(false)} data-testid="upload-done">Done</Button>
          </div>
        )}

        {step === "error" && (
          <div className="space-y-3">
            <div className="flex flex-col items-center py-2">
              <AlertTriangle className="h-12 w-12 text-red-500" />
              <p className="text-sm text-muted-foreground text-center mt-2">{message}</p>
            </div>
            <Button className="w-full rounded-2xl" onClick={() => (result?._id ? startAnalysis() : setStep("preview"))}>Try again</Button>
            <Button variant="ghost" className="w-full rounded-2xl" onClick={() => onOpenChange(false)}>Close</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
